import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AddRounded from '@mui/icons-material/AddRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/DataTable';
import { EmptyState, PageHeader } from '../../../components/states';
import { businessDate, money } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { DateRangeControl } from '../components/DateRangeControl';
import { PageTabs } from '../components/UrlTabs';
import { NoAccess, Panel, QueryError } from '../components/ui';
import { useReportParams } from '../components/useReportParams';
import { useUrlTab } from '../components/useUrlTab';
import {
  ACCOUNT_TYPES,
  groupAccounts,
  naturalBalance,
  ROLE_CHIP,
  sourceLabel,
  type Account,
} from '../lib/accounts';
import { num } from '../lib/numbers';
import { M } from '../reports/common';
import { AccountDialog } from './AccountDialog';

type TabKey = 'accounts' | 'journal' | 'checks';

export function AccountingPage() {
  const { can } = useAuth();
  const canAccounts = can('accounts.manage') || can('journal.view') || can('ledger.view');
  const [tab, setTab] = useUrlTab<TabKey>([
    { value: 'accounts', label: 'Chart of accounts', show: canAccounts },
    { value: 'journal', label: 'Journal', show: can('journal.view') },
    { value: 'checks', label: 'Ledger checks', show: can('ledger.view') },
  ]);
  const [editing, setEditing] = useState<Account | null | undefined>(undefined);
  return (
    <>
      <PageHeader
        title="Accounting"
        subtitle="Your chart of accounts, every posted journal entry and the ledger health checks."
        actions={
          tab === 'accounts' &&
          can('accounts.manage') && (
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => setEditing(null)}>
              Add account
            </Button>
          )
        }
      />
      {!canAccounts ? (
        <NoAccess what="the accounts" />
      ) : (
        <>
          <PageTabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'accounts', label: 'Chart of accounts' },
              { value: 'journal', label: 'Journal', show: can('journal.view') },
              { value: 'checks', label: 'Ledger checks', show: can('ledger.view') },
            ]}
          />
          {tab === 'accounts' && <AccountsTab onEdit={setEditing} />}
          {tab === 'journal' && <JournalTab />}
          {tab === 'checks' && <ChecksTab />}
        </>
      )}
      <AccountDialog
        open={editing !== undefined}
        account={editing ?? null}
        onClose={() => setEditing(undefined)}
      />
    </>
  );
}

function AccountsTab({ onEdit }: { onEdit: (a: Account) => void }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canLedger = can('journal.view');
  const canManage = can('accounts.manage');
  const q = useQuery({ queryKey: ['accounts'], queryFn: () => unwrap(api.GET('/accounts')) });
  if (q.error) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isLoading)
    return (
      <Stack spacing={2.5}>
        {[0, 1].map((i) => (
          <Card key={i} sx={{ p: 2.5 }}>
            <Skeleton width="30%" height={28} />
            {[0, 1, 2, 3].map((j) => (
              <Skeleton key={j} height={44} />
            ))}
          </Card>
        ))}
      </Stack>
    );
  const groups = groupAccounts(q.data?.data ?? []);
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1fr 1fr' },
        alignItems: 'start',
      }}
    >
      {groups.map((g) => {
        const info = ACCOUNT_TYPES.find((t) => t.value === g.type);
        const total = g.accounts.reduce((s, a) => s + naturalBalance(a), 0);
        return (
          <Panel
            key={g.type}
            title={info?.plural}
            subtitle={info?.hint}
            padded={false}
            action={
              <Typography
                variant="subtitle1"
                className="tabular"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                <M v={total} />
              </Typography>
            }
          >
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {g.accounts.map((a) => (
                <Box
                  component="li"
                  key={a.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.25,
                    borderTop: 1,
                    borderColor: 'divider',
                    cursor: canLedger ? 'pointer' : 'default',
                    opacity: a.isActive ? 1 : 0.55,
                    '&:hover': canLedger ? { bgcolor: 'action.hover' } : undefined,
                  }}
                  onClick={
                    canLedger ? () => void navigate(`/accounting/accounts/${a.id}`) : undefined
                  }
                >
                  <Typography
                    variant="body2"
                    className="tabular"
                    color="text.secondary"
                    sx={{ width: 48, flexShrink: 0 }}
                  >
                    {a.code}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {a.name}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                      {a.isPaymentAccount && (
                        <Chip
                          size="small"
                          label="Cash / bank"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {a.role && !a.systemKey && (
                        <Chip
                          size="small"
                          label={ROLE_CHIP[a.role]}
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {!a.isActive && (
                        <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </Stack>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    <M v={naturalBalance(a)} signed />
                  </Typography>
                  {canManage && (
                    <Tooltip title="Edit account">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${a.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(a);
                        }}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canLedger && (
                    <ChevronRightRounded fontSize="small" sx={{ color: 'text.disabled' }} />
                  )}
                </Box>
              ))}
            </Box>
          </Panel>
        );
      })}
    </Box>
  );
}

const SOURCE_TYPES = [
  'SALES_INVOICE',
  'DELIVERY',
  'PAYMENT',
  'SUPPLIER_INVOICE',
  'GOODS_RECEIPT',
  'SALES_RETURN',
  'PURCHASE_RETURN',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
  'STOCK_ADJUSTMENT',
  'OPENING_BALANCE',
  'OPENING_STOCK',
];

function JournalTab() {
  const p = useReportParams('30d');
  const { format } = useShop();
  const navigate = useNavigate();
  const sourceType = p.get('sourceType') ?? undefined;
  const query = { ...p.range, sourceType, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['journal-entries', query],
    queryFn: () => unwrap(api.GET('/journal-entries', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <DataTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(e) => e.id}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        onRowClick={(e) => void navigate(`/accounting/journal/${e.id}`)}
        toolbar={
          <>
            <DateRangeControl
              range={p.range}
              preset={p.preset}
              onPreset={p.setPreset}
              onCustom={p.setCustom}
            />
            <TextField
              select
              size="small"
              fullWidth={false}
              label="Source"
              value={sourceType ?? ''}
              onChange={(e) => p.update({ sourceType: e.target.value || null })}
              sx={{ minWidth: 200 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">All sources</MenuItem>
              {SOURCE_TYPES.map((s) => (
                <MenuItem key={s} value={s}>
                  {sourceLabel(s)}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
        empty={
          <EmptyState
            icon={<MenuBookOutlined />}
            title="No journal entries"
            body="Entries are posted automatically by sales, purchases, payments and stock changes."
          />
        }
        columns={[
          {
            key: 'date',
            header: 'Date',
            width: 130,
            render: (e) => businessDate(e.entryDate, format),
          },
          {
            key: 'memo',
            header: 'Entry',
            render: (e) => (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {e.memo ?? sourceLabel(e.sourceType)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sourceLabel(e.sourceType)} · {e.lines.length} lines
                  {e.reversedById ? ' · reversed' : ''}
                  {e.reversalOfId ? ' · reversal' : ''}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'accounts',
            header: 'Accounts',
            hideBelow: 'md',
            render: (e) => (
              <Typography variant="caption" color="text.secondary">
                {[...new Set(e.lines.map((l) => l.accountName))].join(', ')}
              </Typography>
            ),
          },
          {
            key: 'amount',
            header: 'Amount',
            align: 'right',
            render: (e) => <M v={e.lines.reduce((s, l) => s + num(l.debit), 0)} strong />,
          },
        ]}
      />
    </>
  );
}

function CheckRow({ ok, title, children }: { ok: boolean; title: string; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={1.75}
      sx={{ py: 1.75, borderTop: 1, borderColor: 'divider', alignItems: 'flex-start' }}
    >
      {ok ? (
        <CheckCircleRounded color="success" sx={{ mt: 0.25 }} />
      ) : (
        <ErrorOutlineRounded color="error" sx={{ mt: 0.25 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 650 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

function ChecksTab() {
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['ledger-reconciliation'],
    queryFn: () => unwrap(api.GET('/ledger/reconciliation')),
  });
  if (q.error) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const ctl = (r: NonNullable<typeof d>['receivable'], who: string) => (
    <CheckRow ok={r.ok} title={`${r.account.name} matches ${who} balances`}>
      Control account {money(r.controlBalance, format)} · {r.partiesWithBalance} {who} total{' '}
      {money(r.partyBalanceTotal, format)}
      {num(r.unassignedBalance) !== 0 &&
        ` · ${money(r.unassignedBalance, format)} on ${String(r.unassignedLines)} lines without a party`}
      {!r.ok && ` · difference ${money(r.difference, format)}`}
    </CheckRow>
  );
  return (
    <Panel
      title={
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <span>Ledger checks</span>
          {d && (
            <Chip
              size="small"
              color={d.ok ? 'success' : 'error'}
              label={d.ok ? 'All good' : 'Needs attention'}
              sx={{ fontWeight: 650 }}
            />
          )}
        </Stack>
      }
      subtitle="Automatic checks that the books add up"
      sx={{ maxWidth: 820 }}
    >
      {!d ? (
        [0, 1, 2].map((i) => <Skeleton key={i} height={56} />)
      ) : (
        <>
          <CheckRow ok={d.journal.ok} title="Every journal entry balances">
            Total debits {money(d.journal.totalDebit, format)} · credits{' '}
            {money(d.journal.totalCredit, format)}
            {d.journal.unbalancedEntries > 0 &&
              ` · ${String(d.journal.unbalancedEntries)} unbalanced entries`}
          </CheckRow>
          {ctl(d.receivable, 'customer')}
          {ctl(d.payable, 'supplier')}
          <Stack
            direction="row"
            spacing={1}
            sx={{ pt: 1.5, alignItems: 'center', color: 'text.secondary' }}
          >
            <AccountBalanceOutlined fontSize="small" />
            <Typography variant="caption">
              Checked {d.asOf ? `as of ${businessDate(d.asOf, format)}` : 'just now'}
            </Typography>
          </Stack>
        </>
      )}
    </Panel>
  );
}
